import { useRef, useState } from 'react'
import { ReadyState } from 'react-use-websocket'

const SAMPLE_RATE = 16000 // 16 kHz for speech
const CHANNELS = 1

export function encodeInt16(samples: Float32Array): string {
    const int16Samples = new Int16Array(samples.length)
    for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]))
        int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff
    }
    const bytes = new Uint8Array(int16Samples.buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
}

interface UseAudioRecorderProps {
    sendMessage: (message: string) => void
    readyState: ReadyState
}

export const useAudioRecorder = ({ sendMessage, readyState }: UseAudioRecorderProps) => {
    const [isRecording, setIsRecording] = useState<boolean>(false)

    const audioContextRef = useRef<AudioContext | null>(null)
    const workletNodeRef = useRef<AudioWorkletNode | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const sampleBufferRef = useRef<Float32Array[]>([])
    const isRecordingRef = useRef<boolean>(false)
    const isMutedRef = useRef<boolean>(false)
    const chunkIntervalRef = useRef<NodeJS.Timeout | null>(null)

    const startChunkInterval = () => {
        return setInterval(() => {
            if (
                !isRecordingRef.current ||
                isMutedRef.current ||
                sampleBufferRef.current.length === 0
            ) {
                if (isMutedRef.current) sampleBufferRef.current = []
                return
            }

            const totalLength = sampleBufferRef.current.reduce((sum, arr) => sum + arr.length, 0)
            const concatenated = new Float32Array(totalLength)
            let offset = 0
            for (const chunk of sampleBufferRef.current) {
                concatenated.set(chunk, offset)
                offset += chunk.length
            }

            const pcmBase64 = encodeInt16(concatenated)

            try {
                sendMessage(
                    JSON.stringify({
                        type: 'audio',
                        sampleRate: SAMPLE_RATE,
                        data: pcmBase64,
                    }),
                )
                console.debug(`[AudioRecorder] Sent PCM chunk (${totalLength} samples)`)
            } catch (err) {
                console.error('[AudioRecorder] Failed to send chunk', err)
            }

            sampleBufferRef.current = []
        }, 1000)
    }

    const useFallbackProcessor = (audioContext: AudioContext, stream: MediaStream) => {
        const source = audioContext.createMediaStreamSource(stream)
        const processor = audioContext.createScriptProcessor(4096, CHANNELS, CHANNELS)

        processor.onaudioprocess = (e) => {
            if (isMutedRef.current) return
            const inputData = e.inputBuffer.getChannelData(0)
            sampleBufferRef.current.push(new Float32Array(inputData))
        }

        source.connect(processor)
        processor.connect(audioContext.destination)

        sampleBufferRef.current = []
        isRecordingRef.current = true

        chunkIntervalRef.current = startChunkInterval()
        workletNodeRef.current = processor as unknown as AudioWorkletNode
        setIsRecording(true)
        console.info('[AudioRecorder] Recording started with ScriptProcessorNode (fallback)')
    }

    const handleStartRecording = async (preObtainedStream?: MediaStream) => {
        if (readyState !== ReadyState.OPEN) {
            console.warn('[AudioRecorder] WebSocket not connected')
            return
        }

        try {
            const stream =
                preObtainedStream ??
                (await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        sampleRate: SAMPLE_RATE,
                    },
                }))
            streamRef.current = stream

            const audioContext = new (window.AudioContext || window['webkitAudioContext'])({
                sampleRate: SAMPLE_RATE,
            })
            audioContextRef.current = audioContext

            try {
                await audioContext.audioWorklet.addModule('/audio-processor.js')
            } catch (err) {
                console.warn(
                    '[AudioRecorder] AudioWorklet not supported, falling back to ScriptProcessorNode',
                    err,
                )
                useFallbackProcessor(audioContext, stream)
                return
            }

            const source = audioContext.createMediaStreamSource(stream)
            const workletNode = new AudioWorkletNode(audioContext, 'audio-processor')
            workletNodeRef.current = workletNode

            sampleBufferRef.current = []
            isRecordingRef.current = true

            // Listen for audio data from the worklet
            workletNode.port.onmessage = (event) => {
                if (event.data.type === 'audio' && event.data.data && !isMutedRef.current) {
                    sampleBufferRef.current.push(event.data.data)
                }
            }

            source.connect(workletNode)

            chunkIntervalRef.current = startChunkInterval()
            setIsRecording(true)
            console.info('[AudioRecorder] Recording started with AudioWorklet')
        } catch (error) {
            console.error(
                '[AudioRecorder] Microphone access denied:',
                error?.message || String(error),
            )
        }
    }

    const handleStopRecording = () => {
        if (!workletNodeRef.current || !streamRef.current) return

        isRecordingRef.current = false

        if (chunkIntervalRef.current) {
            clearInterval(chunkIntervalRef.current)
            chunkIntervalRef.current = null
        }

        if (sampleBufferRef.current.length > 0) {
            const totalLength = sampleBufferRef.current.reduce((sum, arr) => sum + arr.length, 0)
            const concatenated = new Float32Array(totalLength)
            let offset = 0
            for (const chunk of sampleBufferRef.current) {
                concatenated.set(chunk, offset)
                offset += chunk.length
            }

            const pcmBase64 = encodeInt16(concatenated)

            try {
                sendMessage(
                    JSON.stringify({
                        type: 'audio',
                        isLastChunk: true,
                        data: pcmBase64,
                    }),
                )
                console.debug(`[AudioRecorder] Sent final PCM chunk (${totalLength} samples)`)
            } catch (err) {
                console.error('[AudioRecorder] Failed to send final audio chunk', err)
            }
        } else {
            try {
                sendMessage(
                    JSON.stringify({
                        type: 'audio',
                        isLastChunk: true,
                    }),
                )
                console.debug('[AudioRecorder] Sent final signal (isLastChunk=true)')
            } catch (err) {
                console.error('[AudioRecorder] Failed to send final signal', err)
            }
        }

        sampleBufferRef.current = []

        if (workletNodeRef.current) {
            workletNodeRef.current.disconnect()
        }
        if (audioContextRef.current) {
            audioContextRef.current.close()
        }

        streamRef.current.getTracks().forEach((track) => track.stop())

        setIsRecording(false)
        console.info('[AudioRecorder] Recording stopped')
    }

    const setMuted = (muted: boolean) => {
        isMutedRef.current = muted
        if (muted) sampleBufferRef.current = []
    }

    return {
        isRecording,
        handleStartRecording,
        handleStopRecording,
        setMuted,
    }
}
